import React, { useState, useEffect } from 'react';
import {
  Hash,
  Calendar,
  Mail,
  Edit2,
  Trash2,
  Plus,
  Save,
  X,
  Info,
  Edit,
  Loader2
} from 'lucide-react';
import './GeneralInfo.css';
import { ApiUrl } from '../API/ApiUrl';
import Toast from "../components/Toast/Toast";
import Swal from "sweetalert2";

const GeneralInfo = () => {
  const [formData, setFormData] = useState({
    issn: '',
    frequency: '',
    email: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success"
  });

  const [infoList, setInfoList] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };


  const fetchGeneralInfo = async () => {
    try {
      // const token = localStorage.getItem("adminToken");

      const response = await fetch(`${ApiUrl}/generalinfo`);

      if (!response.ok) {
        throw new Error("Unauthorized or failed to fetch");
      }

      const data = await response.json();
      setInfoList(data);
      console.log("Fetched General Info:", data);

    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchGeneralInfo();
  }, []);

  const validateForm = () => {
    const { issn, frequency, email } = formData;

    // ISSN → 4 digits - 4 digits
    const issnRegex = /^\d{4}-\d{3}[\dX]$/;

    if (!issnRegex.test(issn)) {
      setToast({
        show: true,
        message: "Invalid ISSN format (e.g., 1234-5678)",
        type: "error"
      });
      return false;
    }

    // Frequency → only letters + spaces
    const frequencyRegex = /^[A-Za-z\s]+$/;

    if (!frequencyRegex.test(frequency)) {
      setToast({
        show: true,
        message: "Frequency should contain only letters (e.g., Monthly)",
        type: "error"
      });
      return false;
    }

    // Email → standard validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|in)$/;
    if (!emailRegex.test(email)) {
      setToast({
        show: true,
        message: "Invalid email format",
        type: "error"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);


    try {
      const token = localStorage.getItem("adminToken");

      let response;

      if (editingId) {
        response = await fetch(`${ApiUrl}/generalinfo/${editingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${ApiUrl}/generalinfo`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
      }

      const data = await response.json();

      if (!response.ok) {
        setToast({
          show: true,
          message: data.message || "Operation failed",
          type: "error"
        });
        return;
      }

      //  SUCCESS TOAST
      setToast({
        show: true,
        message: editingId
          ? "Information updated successfully "
          : "Information added successfully ",
        type: "success"
      });

      // if (!response.ok) {
      //   alert(data.message || "Unauthorized");
      //   return;
      // }

      fetchGeneralInfo();
      setFormData({ issn: "", frequency: "", email: "" });
      setEditingId(null);
      setShowForm(false);

    } catch (error) {
      console.error("Error:", error);
      setToast({
        show: true,
        message: "Server error occurred",
        type: "error"
      });
    }
    setSaving(false);

  };

  const handleEdit = (info) => {
    setFormData({
      issn: info.issn,
      frequency: info.frequency,
      email: info.email
    });
    setEditingId(info.id);
    setShowForm(true);
  };
  const handleDelete = async (id) => {
    const token = localStorage.getItem("adminToken");

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This information will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${ApiUrl}/generalinfo/${id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        setToast({
          show: true,
          message: data.message || "Delete failed",
          type: "error"
        });
        return;
      }

      setToast({
        show: true,
        message: "Information deleted successfully 🎉",
        type: "success"
      });

      fetchGeneralInfo();

    } catch (error) {
      setToast({
        show: true,
        message: "Server error occurred",
        type: "error"
      });
    }
  };

  const handleCancel = () => {
    setFormData({ issn: '', frequency: '', email: '' });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div className="general-info-page">
      {/* Header */}
      <div className="page-header">
        <div className="title-section">
          <Info size={40} className="title-icon" />
          <div>
            <h1 className="page-title">General Information</h1>
            <p className="page-subtitle">Manage journal details (Note: The last entered value will be displayed on the user side)</p>
          </div>
        </div>

        {!showForm && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={16} />
            <span>Add New</span>
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="form-card">
          <div className="form-header">
            <h3>{editingId ? 'Edit Information' : 'Add New Information'}</h3>
            <button className="btn-icon" onClick={handleCancel}>
              <X size={16} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="issn">
                  <Hash size={14} />
                  <span>ISSN <span className="required">*</span></span>
                </label>
                <input
                  type="text"
                  id="issn"
                  name="issn"
                  value={formData.issn}
                  onChange={handleInputChange}
                  placeholder="1234-5678"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="frequency">
                  <Calendar size={14} />
                  <span>Frequency <span className="required">*</span></span>
                </label>
                <input
                  type="text"
                  id="frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  placeholder="Monthly, Quarterly, etc."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={14} />
                  <span>Email <span className="required">*</span></span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="editor@journal.com"
                  required
                />
              </div>
            </div>

            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 size={16} className="spin-icon" />
                  <span>{editingId ? 'Updating...' : 'Saving...'}</span>
                </>
              ) : (
                <>
                  <Save size={14} />
                  <span>{editingId ? 'Update' : 'Save'}</span>
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="table-card">
        <div className="table-header">
          <h3>Information List</h3>
          <span className="record-count">{infoList.length} records</span>
        </div>

        <div className="table-responsive">
          <table className="info-table">
            <thead>
              <tr>
                <th>Sl. No</th>
                <th>ISSN</th>
                <th>Frequency</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {infoList.map((info, index) => (
                <tr key={info.id}>
                  <td>
                    <span className="sl-no">{index + 1}</span>
                  </td>
                  <td>
                    <div className="cell-content">
                      <Hash size={12} className="cell-icon" />
                      {info.issn}
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">
                      <Calendar size={12} className="cell-icon" />
                      {info.frequency}
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">
                      <Mail size={12} className="cell-icon" />
                      {info.email}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        onClick={() => handleEdit(info)}
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(info.id)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default GeneralInfo;